import api, { API_BASE_URL } from '../../../auth/apiClient';
import { isAxiosError } from 'axios';
import { trackDownload } from '../../../activity';

interface PictureResponse {
    id: number;
    path: string;
    server_uri: string;
    blok_inspeksi_id: number;
}

interface InspeksiPopupResponse {
    uuid: string;
    id: number;
    estate: string;
    divisi: string;
    category: string;
    penalty_code: string;
    penalty_description: string;
    pemanen: string;
    pemanen_nomor_induk_pegawai: string;
    pemanen_kemandoran: string;
    user: string;
    emp_pemanen: string;
    emp_pemanen_nomor_induk_pegawai: string;
    emp_pemanen_kemandoran: string;
    job_level: string;
    nomor_induk_pegawai: string;
    job_position: string;
    tanggal: string;
    waktu: string;
    blok: string;
    hasil_inspeksi: string;
    latitude: number;
    longitude: number;
    no_tph: string;
    no_baris: string;
    no_pokok: string;
    qty: number;
    tph: string;
    ripe_qty: number;
    unripe_qty: number;
    rotten_qty: number;
    lstalk_qty: number;
    loose_qty: number;
    abnormal_01_qty: number;
    abnormal_02_qty: number;
    abnormal_03_qty: number;
    abnormal_04_qty: number;
    no_potong: string;
    pictures: PictureResponse[];
}

interface Estate {
    id: number;
    name: string;
}

interface Divisi {
    id: number;
    name: string;
    estate_id: number;
}

interface InspeksiDenganGambarProps {
    startDate: string;
    endDate: string;
    selectedEstate: number | null;
    selectedDivisi: number | null;
    selectedCategory: string;
    selectedUser: string;
    estates: Estate[];
    divisis: Divisi[];
    setIsLoading: (loading: boolean) => void;
    toast: any;
}

const fetchImageAsBuffer = async (url: string): Promise<ArrayBuffer | null> => {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            console.warn(`Failed to fetch image: ${url}`);
            return null;
        }
        return await response.arrayBuffer();
    } catch (error) {
        console.warn(`Error fetching image ${url}:`, error);
        return null;
    }
};

const InspeksiDenganGambar = {
    export: async ({ startDate, endDate, selectedEstate, selectedDivisi, selectedCategory, selectedUser, estates, divisis, setIsLoading, toast }: InspeksiDenganGambarProps) => {

        try {
            const params: any = {
                start_date: startDate,
                end_date: endDate,
            };

            if (selectedEstate) {
                const selectedEstateObj = estates.find(e => e.id === selectedEstate);
                params.estate = selectedEstateObj?.name || '';
            }
            if (selectedDivisi) {
                const selectedDivisiObj = divisis.find(d => d.id === selectedDivisi);
                params.divisi = selectedDivisiObj?.name || '';
            }
            if (selectedCategory) {
                params.category = selectedCategory;
            }
            if (selectedUser) {
                params.users = selectedUser;
            }

            const response = await api.get<InspeksiPopupResponse[]>(
                '/inspeksi/all',
                { params }
            );

            if (!response.data || response.data.length === 0) {
                toast({
                    title: 'Info',
                    description: 'Tidak ada data untuk dilaporkan pada periode terpilih.',
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            const inspectionData = response.data;
            const categoryId = parseInt(selectedCategory);

            // Create a new workbook using ExcelJS
            const ExcelJS = (await import('exceljs')).default;
            const workbook = new ExcelJS.Workbook();

            // Group by estate
            const groupedByEstate = inspectionData.reduce((acc, item) => {
                const estateKey = item.estate || 'UNDEFINED_ESTATE';
                if (!acc[estateKey]) {
                    acc[estateKey] = [];
                }
                acc[estateKey].push(item);
                return acc;
            }, {} as Record<string, InspeksiPopupResponse[]>);

            for (const [estateName, inspections] of Object.entries(groupedByEstate)) {
                const worksheet = workbook.addWorksheet(estateName.substring(0, 30));

                // Define columns based on category
                let columns: any[] = [];

                if (categoryId === 10) {
                    columns = [
                        { header: 'ESTATE', key: 'estate', width: 20 },
                        { header: 'DIVISI', key: 'divisi', width: 18 },
                        { header: 'NIP', key: 'nomor_induk_pegawai', width: 12 },
                        { header: 'PIC', key: 'user', width: 15 },
                        { header: 'JOB LEVEL', key: 'job_level', width: 10 },
                        { header: 'JOB POSITION', key: 'job_position', width: 12 },
                        { header: 'TANGGAL', key: 'tanggal_waktu', width: 18 },
                        { header: 'BLOK', key: 'blok', width: 9 },
                        { header: 'KATEGORI', key: 'category', width: 10 },
                        { header: 'HASIL INSPEKSI', key: 'hasil_inspeksi', width: 16 },
                        { header: 'LOC URL', key: 'loc_url', width: 30 },
                        { header: 'NIP PEMANEN', key: 'pemanen_nomor_induk_pegawai', width: 13 },
                        { header: 'PEMANEN', key: 'pemanen', width: 13 },
                        { header: 'KEMANDORAN', key: 'pemanen_kemandoran', width: 19 },
                        { header: 'PENALTY CODE', key: 'penalty_code', width: 8 },
                        { header: 'PENALTY DESCRIPTION', key: 'penalty_description', width: 23 },
                        { header: 'NO TPH', key: 'no_tph', width: 10 },
                        { header: 'NO BARIS', key: 'no_baris', width: 6 },
                        { header: 'NO POKOK', key: 'no_pokok', width: 7 },
                        { header: 'QTY', key: 'qty', width: 5 },
                        { header: 'GAMBAR', key: 'pictures', width: 47 },
                    ];
                } else if (categoryId === 1 && inspections.some(i => i.pemanen)) {
                    columns = [
                        { header: 'ESTATE', key: 'estate', width: 20 },
                        { header: 'DIVISI', key: 'divisi', width: 18 },
                        { header: 'NIP', key: 'nomor_induk_pegawai', width: 12 },
                        { header: 'PIC', key: 'user', width: 15 },
                        { header: 'JOB LEVEL', key: 'job_level', width: 10 },
                        { header: 'JOB POSITION', key: 'job_position', width: 12 },
                        { header: 'TANGGAL', key: 'tanggal_waktu', width: 18 },
                        { header: 'BLOK', key: 'blok', width: 9 },
                        { header: 'KATEGORI', key: 'category', width: 10 },
                        { header: 'HASIL INSPEKSI', key: 'hasil_inspeksi', width: 16 },
                        { header: 'LOC URL', key: 'loc_url', width: 30 },
                        { header: 'NO POTONG', key: 'no_potong', width: 12 },
                        { header: 'NIP PEMANEN', key: 'pemanen_nomor_induk_pegawai', width: 13 },
                        { header: 'PEMANEN', key: 'pemanen', width: 13 },
                        { header: 'KEMANDORAN', key: 'pemanen_kemandoran', width: 19 },
                        { header: 'PENALTY CODE', key: 'penalty_code', width: 8 },
                        { header: 'PENALTY DESCRIPTION', key: 'penalty_description', width: 23 },
                        { header: 'NO TPH', key: 'no_tph', width: 10 },
                        { header: 'NO BARIS', key: 'no_baris', width: 6 },
                        { header: 'NO POKOK', key: 'no_pokok', width: 7 },
                        { header: 'QTY', key: 'qty', width: 5 },
                        { header: 'GAMBAR', key: 'pictures', width: 47 },
                    ];
                } else if (categoryId === 11) {
                    columns = [
                        { header: 'ESTATE', key: 'estate', width: 20 },
                        { header: 'DIVISI', key: 'divisi', width: 18 },
                        { header: 'NIP', key: 'nomor_induk_pegawai', width: 12 },
                        { header: 'USER', key: 'user', width: 15 },
                        { header: 'JOB LEVEL', key: 'job_level', width: 10 },
                        { header: 'JOB POSITION', key: 'job_position', width: 12 },
                        { header: 'TANGGAL', key: 'tanggal_waktu', width: 18 },
                        { header: 'BLOK', key: 'blok', width: 9 },
                        { header: 'KATEGORI', key: 'category', width: 10 },
                        { header: 'HASIL INSPEKSI', key: 'hasil_inspeksi', width: 16 },
                        { header: 'LOC URL', key: 'loc_url', width: 30 },
                        { header: 'NIP PEMANEN', key: 'pemanen_nomor_induk_pegawai', width: 13 },
                        { header: 'PEMANEN', key: 'pemanen', width: 13 },
                        { header: 'KEMANDORAN', key: 'pemanen_kemandoran', width: 19 },
                        { header: 'TPH', key: 'tph', width: 12 },
                        { header: 'MATANG (JJG)', key: 'ripe_qty', width: 9 },
                        { header: 'MENTAH (JJG)', key: 'unripe_qty', width: 9 },
                        { header: 'BUSUK (JJG)', key: 'rotten_qty', width: 8 },
                        { header: 'BUSUK & JANGKOS (JJG)', key: 'lstalk_qty', width: 12 },
                        { header: 'BRONDOLAN (KG)', key: 'loose_qty', width: 12 },
                        { header: 'TBS < 5 KG (JJG)', key: 'abnormal_01_qty', width: 12 },
                        { header: 'DIMAKAN TIKUS (JJG)', key: 'abnormal_02_qty', width: 14 },
                        { header: 'BUAH BATU (JJG)', key: 'abnormal_03_qty', width: 13 },
                        { header: 'PARTHENOCARPY (JJG)', key: 'abnormal_04_qty', width: 15 },
                        { header: 'GAMBAR', key: 'pictures', width: 47 },
                    ];
                } else {
                    columns = [
                        { header: 'ESTATE', key: 'estate', width: 20 },
                        { header: 'DIVISI', key: 'divisi', width: 18 },
                        { header: 'NIP', key: 'nomor_induk_pegawai', width: 15 },
                        { header: 'USER', key: 'user', width: 20 },
                        { header: 'JOB LEVEL', key: 'job_level', width: 15 },
                        { header: 'JOB POSITION', key: 'job_position', width: 20 },
                        { header: 'TANGGAL', key: 'tanggal_waktu', width: 18 },
                        { header: 'BLOK', key: 'blok', width: 10 },
                        { header: 'KATEGORI', key: 'category', width: 15 },
                        { header: 'HASIL INSPEKSI', key: 'hasil_inspeksi', width: 25 },
                        { header: 'LOC URL', key: 'loc_url', width: 30 },
                        { header: 'GAMBAR', key: 'pictures', width: 47 },
                    ];
                }

                worksheet.columns = columns;

                // Hide columns A, B, and K if categoryId is 10 or 11
                if (categoryId === 10 || categoryId === 11) {
                    worksheet.getColumn(1).hidden = true;  // A
                    worksheet.getColumn(2).hidden = true;  // B
                    worksheet.getColumn(11).hidden = true; // K
                }

                // Style header row
                const headerRow = worksheet.getRow(1);
                headerRow.height = 25;
                headerRow.eachCell((cell) => {
                    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FF4A5A33' }
                    };
                    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });

                // Add data rows
                for (let i = 0; i < inspections.length; i++) {
                    const inspection = inspections[i];
                    const rowNumber = i + 2;

                    // Combine date and time
                    const tanggalWaktu = `${inspection.tanggal} ${inspection.waktu}`;

                    // Create location URL
                    const locUrl = `https://www.google.com/maps?q=${inspection.latitude},${inspection.longitude} `;

                    // Prepare row data based on category
                    let rowData: any = {
                        estate: inspection.estate,
                        divisi: inspection.divisi,
                        nomor_induk_pegawai: inspection.nomor_induk_pegawai,
                        user: inspection.user,
                        job_level: inspection.job_level,
                        job_position: inspection.job_position,
                        tanggal_waktu: tanggalWaktu,
                        blok: inspection.blok,
                        category: inspection.category,
                        hasil_inspeksi: inspection.hasil_inspeksi,
                        loc_url: locUrl,
                        pictures: ''
                    };

                    if (categoryId === 10) {
                        rowData = {
                            ...rowData,
                            pemanen_nomor_induk_pegawai: inspection.emp_pemanen_nomor_induk_pegawai,
                            pemanen: inspection.emp_pemanen,
                            pemanen_kemandoran: inspection.emp_pemanen_kemandoran,
                            penalty_code: inspection.penalty_code,
                            penalty_description: inspection.penalty_description,
                            no_tph: inspection.no_tph,
                            no_baris: inspection.no_baris,
                            no_pokok: inspection.no_pokok,
                            qty: inspection.qty === 0 ? '-' : inspection.qty,
                        };
                    } else if (categoryId === 1 && inspection.pemanen) {
                        rowData = {
                            ...rowData,
                            no_potong: inspection.no_potong,
                            pemanen_nomor_induk_pegawai: inspection.emp_pemanen_nomor_induk_pegawai,
                            pemanen: inspection.emp_pemanen,
                            pemanen_kemandoran: inspection.emp_pemanen_kemandoran,
                            penalty_code: inspection.penalty_code,
                            penalty_description: inspection.penalty_description,
                            no_tph: inspection.no_tph,
                            no_baris: inspection.no_baris,
                            no_pokok: inspection.no_pokok,
                            qty: inspection.qty === 0 ? '-' : inspection.qty,
                        };
                    } else if (categoryId === 11) {
                        rowData = {
                            ...rowData,
                            pemanen_nomor_induk_pegawai: inspection.emp_pemanen_nomor_induk_pegawai,
                            pemanen: inspection.emp_pemanen,
                            pemanen_kemandoran: inspection.emp_pemanen_kemandoran,
                            tph: inspection.tph,
                            ripe_qty: inspection.ripe_qty === 0 ? '-' : inspection.ripe_qty,
                            unripe_qty: inspection.unripe_qty === 0 ? '-' : inspection.unripe_qty,
                            rotten_qty: inspection.rotten_qty === 0 ? '-' : inspection.rotten_qty,
                            lstalk_qty: inspection.lstalk_qty === 0 ? '-' : inspection.lstalk_qty,
                            loose_qty: inspection.loose_qty === 0 ? '-' : inspection.loose_qty,
                            abnormal_01_qty: inspection.abnormal_01_qty === 0 ? '-' : inspection.abnormal_01_qty,
                            abnormal_02_qty: inspection.abnormal_02_qty === 0 ? '-' : inspection.abnormal_02_qty,
                            abnormal_03_qty: inspection.abnormal_03_qty === 0 ? '-' : inspection.abnormal_03_qty,
                            abnormal_04_qty: inspection.abnormal_04_qty === 0 ? '-' : inspection.abnormal_04_qty,
                        };
                    }

                    // Add inspection data
                    const row = worksheet.addRow(rowData);

                    // Set row height to accommodate larger images
                    row.height = 220;

                    // Add borders and wrap text to all cells
                    row.eachCell((cell, colNumber) => {
                        cell.border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' }
                        };

                        // Get column key to determine if it's a numeric column
                        const column = worksheet.getColumn(colNumber);
                        const columnKey = column.key;

                        // List of numeric columns that should be right-aligned
                        const numericColumns = [
                            'qty', 'ripe_qty', 'unripe_qty', 'rotten_qty', 'lstalk_qty',
                            'loose_qty', 'abnormal_01_qty', 'abnormal_02_qty',
                            'abnormal_03_qty', 'abnormal_04_qty'
                        ];

                        if (columnKey && numericColumns.includes(columnKey)) {
                            // Right align for numeric columns
                            cell.alignment = { horizontal: 'right', vertical: 'middle', wrapText: true };

                            // If it's a dash (representing zero), style it differently
                            if (cell.value === '-') {
                                cell.font = { italic: true, color: { argb: 'FF808080' } };
                            }
                        } else {
                            // Left align for text columns
                            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                        }
                    });

                    // Handle pictures
                    if (inspection.pictures && inspection.pictures.length > 0) {
                        const pictureUrls = inspection.pictures.map(p => `${API_BASE_URL}${p.server_uri}`);

                        for (let j = 0; j < pictureUrls.length; j++) {
                            const imageBuffer = await fetchImageAsBuffer(pictureUrls[j]);

                            if (imageBuffer) {
                                const imageId = workbook.addImage({
                                    buffer: imageBuffer,
                                    extension: 'png',
                                });

                                // Calculate column index for images
                                const imageColumnIndex = columns.length - 1 + j;

                                const colWidth = worksheet.getColumn(imageColumnIndex + 1).width || 10;
                                const rowHeight = worksheet.getRow(rowNumber).height || 20;
                                const widthPx = colWidth * 5;      // 1 kolom = 7px
                                const heightPx = rowHeight * 1.33; // 1 baris = 1.33px

                                // Add new column for additional images if needed
                                if (j > 0) {
                                    const newColumn = {
                                        header: `GAMBAR ${j + 1}`,
                                        key: `pictures_${j + 1}`,
                                        width: 47
                                    };

                                    if (!worksheet.getColumn(imageColumnIndex + 1).header) {
                                        worksheet.getColumn(imageColumnIndex + 1).header = newColumn.header;
                                        worksheet.getColumn(imageColumnIndex + 1).width = newColumn.width;

                                        // Style the new header
                                        const headerCell = worksheet.getCell(1, imageColumnIndex + 1);
                                        headerCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                                        headerCell.fill = {
                                            type: 'pattern',
                                            pattern: 'solid',
                                            fgColor: { argb: 'FF4A5A33' }
                                        };
                                        headerCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                                        headerCell.border = {
                                            top: { style: 'thin' },
                                            left: { style: 'thin' },
                                            bottom: { style: 'thin' },
                                            right: { style: 'thin' }
                                        };
                                    }

                                    // Add border to the new image cell
                                    const imageCell = worksheet.getCell(rowNumber, imageColumnIndex + 1);
                                    imageCell.border = {
                                        top: { style: 'thin' },
                                        left: { style: 'thin' },
                                        bottom: { style: 'thin' },
                                        right: { style: 'thin' }
                                    };
                                    imageCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                                }

                                // Place image with proportional sizing
                                const imageCol = imageColumnIndex;
                                const imageRow = rowNumber - 1;

                                // worksheet.addImage(imageId, {
                                //     tl: { col: imageCol + 0.1, row: imageRow + 0.1 },
                                //     ext: { width: 365, height: 292 }
                                // });
                                worksheet.addImage(imageId, {
                                    tl: { col: imageColumnIndex + 0.1, row: rowNumber - 1 + 0.1 },
                                    ext: { width: widthPx, height: heightPx }
                                });
                            }
                        }
                    }
                }

                // Ensure all empty cells have borders
                const maxColumnCount = worksheet.columnCount;
                const maxRowCount = worksheet.rowCount;

                for (let row = 1; row <= maxRowCount; row++) {
                    for (let col = 1; col <= maxColumnCount; col++) {
                        const cell = worksheet.getCell(row, col);
                        if (!cell.border || (!cell.border.top && !cell.border.left && !cell.border.bottom && !cell.border.right)) {
                            cell.border = {
                                top: { style: 'thin' },
                                left: { style: 'thin' },
                                bottom: { style: 'thin' },
                                right: { style: 'thin' }
                            };
                        }
                        // Only set alignment for cells that don't already have it set
                        if (row > 1 && !cell.alignment) {
                            // Get column key to determine if it's a numeric column
                            const column = worksheet.getColumn(col);
                            const columnKey = column.key;

                            // List of numeric columns that should be right-aligned
                            const numericColumns = [
                                'qty', 'ripe_qty', 'unripe_qty', 'rotten_qty', 'lstalk_qty',
                                'loose_qty', 'abnormal_01_qty', 'abnormal_02_qty',
                                'abnormal_03_qty', 'abnormal_04_qty'
                            ];

                            if (columnKey && numericColumns.includes(columnKey)) {
                                cell.alignment = { horizontal: 'right', vertical: 'middle', wrapText: true };
                                // If it's a dash (representing zero), style it differently
                                if (cell.value === '-') {
                                    cell.font = { italic: true, color: { argb: 'FF808080' } };
                                }
                            } else {
                                cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                            }
                        }
                    }
                }
            }

            // Generate and download the file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Laporan Inspeksi Dengan Gambar ${startDate} - ${endDate}.xlsx`;
            trackDownload('Laporan Inspeksi Dengan Gambar', { start_date: startDate, end_date: endDate });
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            toast({
                title: 'Success',
                description: 'Laporan inspeksi dengan gambar berhasil diekspor',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            console.error('Error exporting inspeksi gambar:', error);
            let description = 'Gagal mengekspor laporan inspeksi dengan gambar';

            if (isAxiosError(error) && error.response) {
                description = `Gagal mengekspor laporan: ${error.response.data?.message || error.message}`;
            } else if (error instanceof Error) {
                description = `Gagal mengekspor laporan: ${error.message}`;
            }

            toast({
                title: 'Error',
                description,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw error;
        }
    }
};

export default InspeksiDenganGambar;
